// Import necessary hooks and types
import { useState } from 'react';
import { useAuth } from './useAuth'; // Hook for accessing authentication state
import { Job } from '../types/job'; // Job type definition

// Define the structure of the job data required for creation
interface CreateJobData {
  job_title: string; // Job title
  business: string; // Business name
  contract: string; // Contract type (e.g., CDI, CDD)
  city: string; // City of the job
  postal_code: string; // Postal code of the job
  salary: number; // Salary for the job
  description: string; // Job description
  state: 'active' | 'inactive'; // Job state (active or inactive)
}

/**
 * Custom hook for handling job posting operations.
 */
export function useJobPosting() {
  const [loading, setLoading] = useState(false); // Loading state for async operations
  const [error, setError] = useState<string | null>(null); // Error state for handling failures
  const { token, user } = useAuth(); // Get authentication details from the auth hook

  /**
   * Creates a new job and assigns it to the responsible user.
   * 
   * @param jobData - The job data to be created.
   * @returns An object containing the newly created job and updated responsible data.
   */
  const createJob = async (jobData: CreateJobData): Promise<{ newJob: Job; updatedResponsible: any }> => {
    if (!token || !user) {
      throw new Error('Authentication required'); // Ensure user is authenticated
    }

    try {
      setLoading(true); // Start loading
      setError(null); // Reset any previous errors

      // Step 1: Fetch the list of responsibles
      const responsiblesResponse = await fetch(
        'https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/responsibles',
        {
          headers: {
            'Authorization': `Bearer ${token}` // Include authorization header
          }
        }
      );

      if (!responsiblesResponse.ok) {
        throw new Error('Failed to fetch responsibles');
      }

      const responsibles = await responsiblesResponse.json();

      // Find the responsible associated with the current user
      const currentResponsible = responsibles.find((r: any) =>
        r.email?.toLowerCase() === user.email?.toLowerCase() ||
        r.full_name?.toLowerCase() === user.fullName?.toLowerCase()
      );

      if (!currentResponsible) {
        throw new Error(`No responsible found for user: ${user.email}`);
      }

      // Step 2: Create the job
      const jobResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...jobData,
          business: user.businessName // Ensure the business name comes from the authenticated user
        })
      });

      if (!jobResponse.ok) {
        throw new Error('Failed to create job');
      }

      const newJob = await jobResponse.json(); // Parse the response to get the new job

      // Step 3: Update the responsible with the new job
      const updatedJobIds = Array.isArray(currentResponsible.job_id)
        ? [...currentResponsible.job_id, newJob.id] // Add the new job ID to the existing list
        : [newJob.id];

      const updateData = {
        ...currentResponsible,
        job_id: updatedJobIds // Update the responsible's job assignments
      };

      const updateResponse = await fetch(
        `https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/responsibles/${currentResponsible.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!updateResponse.ok) {
        // If updating the responsible fails, clean up by deleting the created job
        await fetch(`https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job/${newJob.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        throw new Error('Failed to assign job to responsible');
      }

      const updatedResponsible = { ...currentResponsible, job_id: updatedJobIds };
      return { newJob, updatedResponsible }; // Return the new job and updated responsible data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create job';
      setError(errorMessage); // Set the error state
      throw new Error(errorMessage); // Re-throw the error
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return {
    createJob, // Function to create a job
    loading, // Loading state
    error // Error state
  };
}
